import React, { useContext, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { ColumnDef, ColumnSort } from "@tanstack/react-table";
import { lowerCase } from "lodash";
import { useParams } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { buildLink } from "common/routing";
import {
  FilterDescription,
  OrderBy,
  TABLE_NAMES,
  TopLevelTableNames,
} from "common/utils/queryBuilder";
import {
  QuerySavedViewFilterInput,
  SavedView as SavedViewType,
  useGetSavedViewQuery,
  useGetTableConfigQuery,
  useQuerySavedViewDataLazyQuery,
} from "../../../generated/graphql";
import { getDocumentUploadUrl } from "../../../utils/documentUploads";
import { setTitle } from "../../../utils/title";
import { AuthContext } from "../../Authorization/AuthContext";
import { Markdown } from "../../Common/__styles__/Markdown";
import {
  ACTION_COLUMN_DEF_CONSTANTS,
  ActionCell,
} from "../../Common/ActionCell";
import { FullWidthTable } from "../../Common/FullWidthTable/FullWidthTable";
import { Attribute } from "../../Common/FullWidthTable/types";
import {
  initializeColumns,
  MinimalColumnDef,
} from "../../Common/FullWidthTable/utils";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../../Common/Tables/hooks";
import { buildColumnDefinitions } from "../../Common/Tables/utils";
import { QueryFileTableResult } from "../../DocumentUploads/__queries__/table";
import { getFilesTableCustomCells } from "../../DocumentUploads/FilesTable";
import { ActionsProps } from "../../Inputs/DropdownMenu";
import { OBJECTS_TABLE_CUSTOM_CELLS } from "../../ObjectsTable";
import { QueryPropertyTableResult } from "../../Property/__queries__/table";
import { PROPERTY_TABLE_CUSTOM_CELLS } from "../../Property/PropertiesTable";
import { QuerySubmissionTableResult } from "../../Submissions/__queries__/table";
import { SUBMISSION_TABLE_CUSTOM_CELLS } from "../../Submissions/SubmissionsTable";
import { Loading } from "../__styles__/Guest";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { PageSubheader } from "../PageSubheader";
import { accountTitle } from "../utils";

const getDefaultSort = ({
  savedViewOrderBy,
}: {
  savedViewOrderBy: OrderBy;
}): [ColumnSort] => {
  return [
    {
      id: `${savedViewOrderBy.table}.${savedViewOrderBy.field}`,
      desc: savedViewOrderBy.direction === "DESC",
    },
  ];
};

const SavedView = ({
  savedView,
}: {
  savedView: Omit<SavedViewType, "tableType">;
}) => {
  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetTableConfigQuery({
      variables: {
        ...savedView.query.source,
        savedViewId: savedView.id,
      },
      fetchPolicy: "network-only",
    });

  const [
    querySavedView,
    {
      previousData,
      data: currentData,
      networkStatus,
      loading: loadingQuerySavedView,
      error,
    },
  ] = useQuerySavedViewDataLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  setTitle(`${savedView.name} | ${accountTitle()}`);

  const tableName = savedView.query.source.table as TopLevelTableNames;
  const defaultSort = getDefaultSort({
    savedViewOrderBy: savedView.query.orderBy,
  });

  const savedViews = useMemo(() => [savedView], [savedView]);

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    source: savedView.query.source,
    savedViews,
    defaultSort,
  });

  const noun = lowerCase(tableName);
  const loadingDetails = {
    loading: loadingQuerySavedView,
    loadingText: `Loading ${noun}`,
    noDataText: `No ${noun} found`,
  };

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getTableConfig.data ?? [],
    ignoreDefaultSize: true,
    cellRenderers: {
      ...PROPERTY_TABLE_CUSTOM_CELLS,
      ...getFilesTableCustomCells({ isPublic: true }), // Guest component is always public
      ...OBJECTS_TABLE_CUSTOM_CELLS,
      ...SUBMISSION_TABLE_CUSTOM_CELLS({ tableName }),
    },
  });

  const actionsColumn: MinimalColumnDef<
    [{ table: TABLE_NAMES; name: string }]
  > = {
    ...ACTION_COLUMN_DEF_CONSTANTS,
    cell: ({ row }) => {
      return (
        <ActionButton
          id={row.original[`${tableName}.id`]}
          externalDocumentURL={row.original[`Files.externalDocumentURL`]}
          tableName={tableName}
          customMapId={row.original["CustomMapGeometries.customMapId"]}
        />
      );
    },
  };

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.querySavedViewData.pageInfo.totalPages,
    previousTotalPages: previousData?.querySavedViewData.pageInfo.totalPages,
  });

  const tanstackColumnDefinitions: Array<
    ColumnDef<
      | QueryPropertyTableResult
      | QuerySubmissionTableResult
      | QueryFileTableResult
    >
  > = [...generatedColumnDefinitions, actionsColumn];

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn: actionsColumn,
  });

  if (loadingColumnConfig || !columnConfigResponse) {
    return null;
  }

  const tableConfig = columnConfigResponse.getTableConfig.data;

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR",
  );

  const search = ({ page }: { page: number }) => {
    // Only send filters that were added on top of the saved view's base filters
    const baseFilters: FilterDescription[] = savedView.query.filters;
    const additionalFilters: QuerySavedViewFilterInput[] =
      queryDescription.filters
        .filter(
          filter =>
            !baseFilters.some(
              base =>
                base.table === filter.table &&
                base.field === filter.field &&
                base.operator === filter.operator &&
                base.value === filter.value,
            ),
        )
        .map(filter => ({
          table: filter.table,
          field: filter.field,
          operator: filter.operator,
          value: filter.value?.toString() ?? null,
        }));

    void querySavedView({
      variables: {
        savedViewId: savedView.id,
        additionalFilters,
        page,
      },
    });
  };

  return (
    <Layout hasGutters>
      <PageSubheader title={savedView.name} showDivider={false}>
        {savedView.description ?
          <Markdown markdown={savedView.description} />
        : null}
      </PageSubheader>
      <FullWidthTable
        columns={initialColumns}
        previousData={previousData?.querySavedViewData.data}
        currentData={data?.querySavedViewData.data ?? []}
        loadingDetails={loadingDetails}
        manualPaginationConfig={{
          ...manualPaginationConfig,
          pageCount: data?.querySavedViewData.pageInfo.totalPages ?? 1,
        }}
        initialState={initialTableState}
        filterable={{
          filterConfiguration: tableConfig as Array<Attribute>,
          search,
        }}
        timeoutError={timeoutError}
        queryDescription={queryDescription}
        currentView={currentView}
        updateQueryDescription={updateQueryDescription}
        refetchSavedViews={() => {}}
        refetchTable={() => {}}
        clientSideColumnConfig={tableConfig}
        dataSourceComputationsAffectingQuery={
          data?.querySavedViewData.dataSourceComputationsAffectingQuery
        }
        invalidQueryConfig={data?.querySavedViewData.invalidQueryConfig ?? null}
        interactiveHeaders={false}
        rowIdField={`${tableName}.id`}
        totalCount={data?.querySavedViewData.pageInfo.totalCount ?? 0}
      />
    </Layout>
  );
};

const ActionButton = ({
  id,
  tableName,
  externalDocumentURL,
  customMapId,
}: {
  id: string;
  externalDocumentURL: Maybe<string>;
  tableName: TopLevelTableNames;
  customMapId?: Maybe<string>;
}) => {
  const { isFeatureEnabled } = useContext(AuthContext);
  let href: string;

  switch (tableName) {
    case TABLE_NAMES.PROPERTIES:
      href = buildLink("residentProperty", { propertyId: id });
      break;
    case TABLE_NAMES.FILES:
      href = getDocumentUploadUrl({
        id,
        externalDocumentURL,
        isPublic: true,
        intendedRoute: "guestDocumentUploadFile",
      });
      break;
    case TABLE_NAMES.CUSTOM_MAP_GEOMETRIES:
      if (
        !isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS) ||
        !customMapId
      ) {
        return null;
      }
      href = buildLink("residentCustomObject", {
        customMapId,
        geometryId: id,
      });
      break;
    default:
      href = buildLink("viewSubmission", { submissionId: id });
      break;
  }

  const actions: Array<ActionsProps> = [
    {
      label: "View",
      href,
      target: "_blank",
    },
  ];
  return <ActionCell actions={actions} />;
};

export default () => {
  const { savedViewId } = useParams<{ savedViewId: string }>();

  const { data, loading: loadingSavedView } = useGetSavedViewQuery({
    variables: { savedViewId },
  });

  if (loadingSavedView) {
    return <Loading />;
  }

  if (!data?.account?.savedView) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.SAVED_VIEW} />;
  }

  return (
    <SavedView
      savedView={{
        ...data.account.savedView,
        isDefault: true,
        hiddenFromPublic: false,
      }}
    />
  );
};
